import { TZDate } from '@date-fns/tz';

/**
 * The next few times a schedule will fire, computed in the schedule's own
 * timezone so the drawer can show them while the user is still editing.
 *
 * This mirrors the server's scheduleUtils.calculateNextExecution rather than
 * calling it: a round trip per keystroke would make the control feel dead.
 * The server's stored `nextScheduled` stays authoritative — this is a preview,
 * and the drawer labels it as one.
 */

const DEFAULT_CLOSE_DAY = 15;
const MAX_DAYS_SCANNED = 800; // ~2 years: enough for any monthly anchor

const daysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

/** Last Mon–Fri of a month. No holiday calendar, matching the server. */
function lastBusinessDay(year, month) {
    let day = daysInMonth(year, month);
    for (;;) {
        const dow = new Date(Date.UTC(year, month, day)).getUTCDay();
        if (dow !== 0 && dow !== 6) return day;
        day -= 1;
    }
}

function firesOn(trigger, { year, month, day, dow }, closeDay) {
    const inMonth = daysInMonth(year, month);
    if (trigger.frequency === 'daily' || trigger.frequency === 'weekly') {
        return (trigger.days || []).includes(dow);
    }
    if (trigger.frequency === 'monthly') {
        if (trigger.anchor === 'lastBusinessDay') return day === lastBusinessDay(year, month);
        if (trigger.anchor === 'afterClose') return day === Math.min(Number(closeDay) + 1, inMonth);
        const listed = trigger.days || [];
        if (listed.includes(day)) return true;
        // a day this month does not have lands on its last day instead
        return day === inMonth && listed.some((d) => d > inMonth);
    }
    return false;
}

/**
 * @param {object} trigger - { frequency, days, times, timezone, anchor }
 * @param {object} [opts] - { count, from, closeDay }
 * @returns {Date[]} instants, soonest first
 */
export function nextRuns(trigger, { count = 3, from = new Date(), closeDay = DEFAULT_CLOSE_DAY } = {}) {
    const times = [...(trigger?.times || [])].filter((t) => /^\d{2}:\d{2}$/.test(t)).sort();
    const tz = trigger?.timezone;
    if (!trigger?.frequency || !times.length || !tz) return [];

    const out = [];
    const cursor = new TZDate(from.getTime(), tz);
    let year = cursor.getFullYear();
    let month = cursor.getMonth();
    let day = cursor.getDate();

    for (let scanned = 0; scanned < MAX_DAYS_SCANNED && out.length < count; scanned += 1) {
        const dow = new TZDate(year, month, day, 12, 0, 0, tz).getDay();
        if (firesOn(trigger, { year, month, day, dow }, closeDay)) {
            for (const time of times) {
                const [hours, minutes] = time.split(':').map(Number);
                const when = new TZDate(year, month, day, hours, minutes, 0, tz);
                if (when.getTime() > from.getTime()) {
                    out.push(new Date(when.getTime()));
                    if (out.length >= count) break;
                }
            }
        }
        day += 1;
        if (day > daysInMonth(year, month)) {
            day = 1;
            month += 1;
            if (month > 11) { month = 0; year += 1; }
        }
    }
    return out;
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ORDINAL = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/** "6:00 AM" from "06:00". */
export function formatTime(value) {
    const [h, m] = String(value || '').split(':').map(Number);
    if (!Number.isFinite(h)) return value;
    const suffix = h < 12 ? 'AM' : 'PM';
    const hour = h % 12 === 0 ? 12 : h % 12;
    return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

const list = (items) => (items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/** The two halves every readback needs: when in the day, and on which days. */
function scheduleParts(trigger) {
    if (!trigger?.frequency || !(trigger.times || []).length) return null;
    const times = list([...trigger.times].sort().map(formatTime));
    const tz = trigger.timezone ? `, ${trigger.timezone}` : '';

    if (trigger.frequency === 'monthly') {
        if (trigger.anchor === 'lastBusinessDay') return { times, when: 'on the last business day of each month', tz };
        if (trigger.anchor === 'afterClose') return { times, when: 'the day after the books close', tz };
        const days = list([...(trigger.days || [])].sort((a, b) => a - b).map(ORDINAL));
        return { times, when: `on the ${days} of each month`, tz };
    }
    const days = [...(trigger.days || [])].sort((a, b) => a - b);
    if (days.length === 7) return { times, when: 'every day', tz };
    if (days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d))) {
        return { times, when: 'every weekday', tz };
    }
    return { times, when: `on ${list(days.map((d) => DOW_LABELS[d]))}`, tz };
}

/** One plain sentence describing when a schedule fires. */
export function describeSchedule(trigger) {
    const parts = scheduleParts(trigger);
    if (!parts) return '';
    return `Sends at ${parts.times} ${parts.when}${parts.tz}.`;
}

/** The same schedule as a table cell: "Every day · 8:00 AM". */
export function summarizeSchedule(trigger) {
    const parts = scheduleParts(trigger);
    if (!parts) return 'No schedule';
    const when = parts.when.replace(/^on the /, '').replace(/^on /, '').replace(/^the /, '');
    return `${when.charAt(0).toUpperCase()}${when.slice(1)} · ${parts.times}`;
}
